import {
  ConversationFlow,
  ConversationStep,
  matchFlow,
} from './conversationFlows'
import { generateChecklist } from './checklistTemplates'
import { ChecklistItem } from '../components/ChecklistCard'

export interface ConversationState {
  flow: ConversationFlow | null
  currentStepIndex: number
  collectedData: Record<string, string>
  isComplete: boolean
}

export interface ConversationResponse {
  message: string
  widget?: {
    type: ConversationStep['type']
    question: string
    options?: any[]
    placeholder?: string
    optional?: boolean
  }
  checklist?: {
    title: string
    items: ChecklistItem[]
  }
  isComplete: boolean
}

export class ConversationEngine {
  private state: ConversationState = {
    flow: null,
    currentStepIndex: 0,
    collectedData: {},
    isComplete: false,
  }

  // Start a new conversation based on user input
  startConversation(userInput: string): ConversationResponse {
    const flow = matchFlow(userInput)

    if (!flow) {
      return {
        message:
          'Mi dispiace, non ho capito bene. Puoi riformulare? Ad esempio: "Devo iscrivere mio figlio a scuola" o "Voglio cambiare pediatra".',
        isComplete: true,
      }
    }

    this.state = {
      flow,
      currentStepIndex: 0,
      collectedData: {},
      isComplete: false,
    }

    // Return first step
    return this.getCurrentStep()
  }

  // Process widget response and move to next step
  processResponse(response: string): ConversationResponse {
    if (!this.state.flow) {
      return {
        message: 'Errore: nessuna conversazione attiva.',
        isComplete: true,
      }
    }

    const currentStep = this.state.flow.steps[this.state.currentStepIndex]

    // Store the response
    this.state.collectedData[currentStep.dataKey] = response

    // Move to next step
    this.state.currentStepIndex++

    // Check if conversation is complete
    if (this.state.currentStepIndex >= this.state.flow.steps.length) {
      return this.completeConversation()
    }

    // Return next step
    return this.getCurrentStep()
  }

  // Skip optional step
  skipStep(): ConversationResponse {
    if (!this.state.flow) {
      return {
        message: 'Errore: nessuna conversazione attiva.',
        isComplete: true,
      }
    }

    const currentStep = this.state.flow.steps[this.state.currentStepIndex]

    if (!currentStep.optional) {
      return {
        message: 'Questo passaggio è obbligatorio.',
        widget: {
          type: currentStep.type,
          question: currentStep.question,
          options: currentStep.options,
          placeholder: currentStep.placeholder,
          optional: currentStep.optional,
        },
        isComplete: false,
      }
    }

    // Move to next step without storing data
    this.state.currentStepIndex++

    // Check if conversation is complete
    if (this.state.currentStepIndex >= this.state.flow.steps.length) {
      return this.completeConversation()
    }

    // Return next step
    return this.getCurrentStep()
  }

  // Get current step as a response
  private getCurrentStep(): ConversationResponse {
    if (!this.state.flow) {
      return {
        message: 'Errore: nessuna conversazione attiva.',
        isComplete: true,
      }
    }

    const step = this.state.flow.steps[this.state.currentStepIndex]

    return {
      message: this.getStepMessage(),
      widget: {
        type: step.type,
        question: step.question,
        options: step.options,
        placeholder: step.placeholder,
        optional: step.optional,
      },
      isComplete: false,
    }
  }

  // Generate contextual message for current step
  private getStepMessage(): string {
    if (!this.state.flow) return ''

    if (this.state.currentStepIndex === 0) {
      return 'Perfetto! Ti aiuto a organizzare tutto. Iniziamo:'
    }

    return 'Ottimo! Prossima domanda:'
  }

  // Complete conversation and generate checklist
  private completeConversation(): ConversationResponse {
    if (!this.state.flow) {
      return {
        message: 'Errore: nessuna conversazione attiva.',
        isComplete: true,
      }
    }

    const checklist = generateChecklist(
      this.state.flow.templateId,
      this.state.collectedData,
    )

    if (!checklist) {
      return {
        message: 'Errore nella generazione della checklist.',
        isComplete: true,
      }
    }

    this.state.isComplete = true

    return {
      message: 'Ecco la tua checklist personalizzata! 🎯',
      checklist,
      isComplete: true,
    }
  }

  // Reset conversation
  reset() {
    this.state = {
      flow: null,
      currentStepIndex: 0,
      collectedData: {},
      isComplete: false,
    }
  }

  // Get current state (for debugging or persistence)
  getState(): ConversationState {
    return { ...this.state }
  }
}
